import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { Property } from '@shared/definitions/property';
import { PropertyService } from '@core/services/data/properties.service';

/**
 * Phase 4 — signal-based store for property list + currently-viewed
 * property. Mirrors the pattern of AuthStore / WorkspaceStore.
 *
 * Reads come from the signals below. Write actions (addProperty / updateProperty
 * / deleteProperty / updatePropertyOptimistic) delegate to PropertyService via a
 * lazy Injector lookup (same pattern as LoanStore / TenantStore / ContractStore)
 * — avoids the circular DI that would happen if the store injected the service
 * directly while the service injects the store for the read-path bridge.
 */
@Injectable({ providedIn: 'root' })
export class PropertyStore {
  private readonly _list = signal<readonly Property[]>([]);
  private readonly _currentId = signal<string | null>(null);
  private readonly _loading = signal(false);

  private readonly injector = inject(Injector);
  private _service: PropertyService | null = null;
  private get service(): PropertyService {
    return (this._service ??= this.injector.get(PropertyService));
  }

  readonly list = this._list.asReadonly();
  readonly currentId = this._currentId.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly count = computed(() => this._list().length);

  /**
   * O(1) id→entity index, rebuilt (via computed) only when the list changes.
   * Keeps `getById` off an O(n) linear scan — the cockpit calls it per card on
   * every change-detection pass, so the scan was effectively O(cards × properties).
   */
  private readonly _byId = computed(() => {
    const index = new Map<string, Property>();
    for (const p of this._list()) index.set(p.propertyUID, p);
    return index;
  });

  readonly current = computed(() => {
    const id = this._currentId();
    if (!id) return null;
    return this._byId().get(id) ?? null;
  });

  /** Synchronous lookup. Returns null when the store hasn't loaded or no match. */
  getById(propertyUID: string): Property | null {
    return this._byId().get(propertyUID) ?? null;
  }

  /** Synchronous display-name lookup. */
  getNameById(propertyUID: string): string {
    return this.getById(propertyUID)?.name ?? 'Unknown Property';
  }

  /** Nullable-id variant matching the legacy `PropertyService.getPropertyNameByUID` API. */
  getPropertyNameByUID(propertyUID: string | null | undefined): string {
    if (!propertyUID) return 'Unknown Property';
    return this.getNameById(propertyUID);
  }

  setList(properties: readonly Property[]): void {
    this._list.set(properties);
  }

  upsert(property: Property): void {
    const list = this._list();
    const i = list.findIndex(p => p.propertyUID === property.propertyUID);
    if (i === -1) {
      this._list.set([...list, property]);
    } else {
      const next = list.slice();
      next[i] = property;
      this._list.set(next);
    }
  }

  remove(propertyId: string): void {
    this._list.set(this._list().filter(p => p.propertyUID !== propertyId));
    if (this._currentId() === propertyId) {
      this._currentId.set(null);
    }
  }

  setCurrentId(id: string | null): void {
    this._currentId.set(id);
  }

  setLoading(value: boolean): void {
    this._loading.set(value);
  }

  reset(): void {
    this._list.set([]);
    this._currentId.set(null);
    this._loading.set(false);
  }

  /** Create a property. Returns the new propertyUID. */
  addProperty(property: Omit<Property, 'propertyUID'>): Observable<string> {
    return this.service.addProperty(property);
  }

  /** Patch an existing property. */
  updateProperty(propertyUID: string, propertyData: Partial<Property>): Observable<void> {
    return this.service.updateProperty(propertyUID, propertyData);
  }

  /** Optimistic patch — returns the updated Property snapshot. */
  updatePropertyOptimistic(propertyUID: string, propertyData: Partial<Property>): Observable<Property> {
    return this.service.updatePropertyOptimistic(propertyUID, propertyData);
  }

  /** Delete a property (cascade-deletes via backend). */
  deleteProperty(propertyUID: string): Observable<void> {
    return this.service.deleteProperty(propertyUID);
  }
}
